import dayjs from 'dayjs';
import { useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  modifyCloudIdentityGroupMemberExpiry,
  UserKeys,
} from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { Messages } from '../../../../utils/message-constants';
import { handleErrors } from '../../../../utils/errors';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { userPortalPagesConfig } from '../../../../shared/user-portal-pages-config';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Check from '@mui/icons-material/Check';

interface GroupMembershipExpirationChipProps {
  groupEmail?: string;
  memberEmail: string;
  groupId?: string;
  membershipId?: string;
  currentExpirationTime: string;
  memberRole?: string | null;
  onExpirationChange?: (newExpirationTime: string | null) => void;
}

export default function GroupMembershipExpirationChip({
  groupEmail,
  memberEmail,
  currentExpirationTime,
  groupId,
  membershipId,
  memberRole,
  onExpirationChange,
}: GroupMembershipExpirationChipProps) {
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { directoryRoles, changeLogRoles } = useUserSettings();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );
  const [isOpen, setIsOpen] = useState(false);
  const { setSnackbar } = useStore();
  const [expirationTime, setExpirationTime] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [mutationType, setMutationType] = useState<
    'create' | 'update' | 'delete' | null
  >(null);
  const [requestData, setRequestData] =
    useState<cloudidentity_v1.Schema$ModifyMembershipRolesRequest>({});

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const hasGroupmemberExpiryRole = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.EditGroupMemberExpiry.value
  );
  const expiryRoleTitle =
    hasGroupmemberExpiryRole === false
      ? Messages.Errors.Permission.Acting(
          DirectoryRoles.EditGroupMemberExpiry.name
        )
      : '';

  function handleSubmit(type: 'create' | 'delete' | 'update') {
    if (onExpirationChange) {
      if (type === 'delete') {
        onExpirationChange(null);
      } else {
        onExpirationChange(expirationTime);
      }
      handleMenuClose();
      return;
    }

    setMutationType(type);
    const payload: cloudidentity_v1.Schema$ModifyMembershipRolesRequest = {
      updateRolesParams: [
        {
          fieldMask: 'expiry_detail.expire_time',
          membershipRole: {
            expiryDetail: {
              ...(type !== 'delete' && {
                expireTime: expirationTime,
              }),
            },
            name: 'MEMBER',
          },
        },
      ],
    };
    setRequestData(payload);
    if (
      !userView &&
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupMemberExpiry.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (groupEmail && groupId && membershipId) {
        mutate({
          request: payload,
          groupEmail,
          groupId,
          membershipId,
          memberEmail,
          changeLogMessage: '',
        });
      }
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: {
      request: cloudidentity_v1.Schema$ModifyMembershipRolesRequest;
      groupEmail: string;
      groupId: string;
      membershipId: string;
      memberEmail: string;
      changeLogMessage: string;
    }) =>
      modifyCloudIdentityGroupMemberExpiry(
        data.request,
        data.groupEmail,
        data.groupId,
        data.membershipId,
        data.memberEmail,
        data.changeLogMessage
      ),
    onSuccess: (_data, variables) => {
      if (!groupEmail || !groupId || !membershipId) {
        return;
      }

      queryClient.setQueryData<
        cloudidentity_v1.Schema$Membership[] | undefined
      >([UserKeys.GroupMembersCloudIdentity, groupEmail], (oldData) => {
        if (!oldData) {
          return oldData;
        }

        const newMemberships = oldData.map((membership) => {
          if (
            membership.name === `groups/${groupId}/memberships/${membershipId}`
          ) {
            const updatedRoles = membership.roles?.map((role) => {
              if (role.name === 'MEMBER') {
                return {
                  ...role,
                  expiryDetail: {
                    expireTime:
                      variables.request.updateRolesParams?.[0].membershipRole
                        ?.expiryDetail?.expireTime || null,
                  },
                };
              }
              return role;
            });
            return { ...membership, roles: updatedRoles };
          }
          return membership;
        });

        return newMemberships;
      });
      setChangeLogMessage('');
      setRequestData({});
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated(`${memberEmail}'s expiry date`),
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          `${memberEmail}'s expiry date`
        ),
      });
    },
    onSettled: () => {
      setOpenChangeLogDialog(false);
      setMutationType(null);
      handleMenuClose();
    },
  });

  const expiryDateLabel = currentExpirationTime
    ? dayjs(currentExpirationTime).format('MMM D, YYYY h:mm A')
    : 'Click to set expiration';

  if (memberRole === 'OWNER' || memberRole === 'MANAGER') {
    return (
      <Chip
        label={
          memberRole
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ') + "s can't have an expiry date"
        }
        size="small"
        sx={{
          width: 'fit-content',
          cursor: 'not-allowed',
        }}
        disabled
      />
    );
  }

  return (
    <>
      <Tooltip
        title={expiryRoleTitle}
        children={
          <span>
            <Chip
              label={expiryDateLabel}
              size="small"
              sx={{
                width: 'fit-content',
                cursor: 'pointer',
              }}
              color={currentExpirationTime ? 'primary' : 'default'}
              disabled={
                hasGroupmemberExpiryRole === false ||
                (mutationType === 'delete' && isPending)
              }
              onClick={(event) => {
                setExpirationTime(
                  currentExpirationTime ||
                    dayjs().add(1, 'M').endOf('day').toISOString()
                );
                setIsOpen(true);
                setAnchorEl(event.currentTarget);
                event.stopPropagation();
              }}
              onDelete={
                currentExpirationTime
                  ? (event) => {
                      event.stopPropagation();
                      handleSubmit('delete');
                    }
                  : undefined
              }
              deleteIcon={
                mutationType === 'delete' && isPending ? (
                  <CircularProgress size={15} color="inherit" />
                ) : undefined
              }
            />
          </span>
        }
      />
      <Menu
        id="date-picker-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              width: 290,
            },
          },
        }}
      >
        <Stack sx={{ p: 1 }} flexDirection="row" gap={1} alignItems="center">
          <DateTimePicker
            label="Expiration Date"
            views={['year', 'month', 'day', 'hours', 'minutes']}
            sx={{ flexGrow: 1 }}
            value={dayjs(expirationTime)}
            timezone="system"
            disablePast
            onChange={(e) => {
              if (e) {
                setExpirationTime(e.toISOString());
              }
            }}
            slotProps={{
              textField: {
                error: false,
              },
            }}
          />
          {isPending ? (
            <Box sx={{ px: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                if (currentExpirationTime === '') {
                  handleSubmit('create');
                } else {
                  handleSubmit('update');
                }
              }}
            >
              {isPending ? <CircularProgress size={15} /> : <Check />}
            </IconButton>
          )}
        </Stack>
      </Menu>
      {openChangeLogDialog ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            if (groupEmail && groupId && membershipId) {
              mutate({
                request: requestData,
                groupEmail,
                groupId,
                membershipId,
                memberEmail,
                changeLogMessage,
              });
            }
          }}
          mutateLoading={isPending}
        />
      ) : null}
    </>
  );
}
